import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Header from './components/Header';
import DirectChatHero from './components/DirectChatHero';

import CompanyRegistrationModal from './components/CompanyRegistrationModal';
import WelcomePopup from './components/WelcomePopup';
import ChatPopup from './components/ChatPopup';
import CategoryPage from './pages/CategoryPage';
import RegistrationPage from './pages/RegistrationPage';
import NewsPage from './pages/NewsPage';
import DashboardPage from './pages/DashboardPage';
import { AuthProvider } from './hooks/useAuth';
import './App.css';
import './components/dashboard/Dashboard.css';
import './components/DirectChatHero.css';

// Página de inicio
const HomePage: React.FC = () => {
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);

  useEffect(() => {
    // Agregar event listeners para los modales
    const handleModalToggle = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.getAttribute('data-uk-toggle') === 'target: #registro-modal') {
        e.preventDefault();
        setIsRegistrationModalOpen(true);
      }
    };

    // Escuchar el evento personalizado para abrir el modal
    const handleOpenRegistrationModal = () => {
      setIsRegistrationModalOpen(true);
    };

    document.addEventListener('click', handleModalToggle);
    document.addEventListener('openRegistrationModal', handleOpenRegistrationModal);

    return () => {
      document.removeEventListener('click', handleModalToggle);
      document.removeEventListener('openRegistrationModal', handleOpenRegistrationModal);
    };
  }, []);

  return (
    <main className="page-main">
      <DirectChatHero />
      <CompanyRegistrationModal 
        isOpen={isRegistrationModalOpen} 
        onClose={() => setIsRegistrationModalOpen(false)} 
      />
    </main>
  );
};

// Página de carga
const LoadingPage: React.FC = () => {
  return (
    <div id="page-preloader" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 25%, #0f0f0f 50%, #1a1a1a 75%, #0a0a0a 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div className="logo-spinner" style={{
        width: '60px',
        height: '60px',
        animation: 'spin 1.5s ease-in-out infinite',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <img 
          src="/assets/img/favicon/favicon.png" 
          alt="BuenAire Logo" 
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      </div>
    </div>
  );
};

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const welcomePopupTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Simular tiempo de carga
    const timer = setTimeout(() => {
      setIsLoading(false);
      
      // Verificar si debe mostrar el popup de bienvenida
      const hasVisitedBefore = localStorage.getItem('buenaire_has_visited');
      if (!hasVisitedBefore) {
        // Mostrar el popup de bienvenida después de un delay para asegurar que el componente esté montado
        welcomePopupTimeoutRef.current = setTimeout(() => {
          setShowWelcomePopup(true);
        }, 2000);
      }
    }, 2000);

    return () => {
      clearTimeout(timer);
      if (welcomePopupTimeoutRef.current) {
        clearTimeout(welcomePopupTimeoutRef.current);
      }
    };
  }, []);



  // Escuchar el evento personalizado para mostrar el popup
  useEffect(() => {
    const handleShowWelcomePopup = () => {
      setShowWelcomePopup(true);
    };

    document.addEventListener('showWelcomePopup', handleShowWelcomePopup);

    return () => {
      document.removeEventListener('showWelcomePopup', handleShowWelcomePopup);
    };
  }, []);



  const handleWelcomePopupClose = () => {
    setShowWelcomePopup(false);
    // Marcar que el usuario ya ha visitado el sitio
    localStorage.setItem('buenaire_has_visited', 'true');
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <AuthProvider>
      <Router>
      {/* Fondo fijo que no se mueve con el scroll */}
      <div 
        className="fixed-background"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: 'url(/assets/img/bg/background.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          zIndex: 0
        }}
      >
        <div 
          className="custom-overlay"
          style={{
            backgroundImage: 'url(/assets/img/bg/overlay.png)',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.8,
            pointerEvents: 'none'
          }}
        ></div>
      </div>

      {/* Contenido de la aplicación */}
      <div className="App" style={{ position: 'relative', zIndex: 1 }}>
        {/* Header siempre visible */}
        <div style={{ position: 'relative', zIndex: 3 }}>
          <Header />
        </div>
        
        {/* Contenido principal con navegación SPA */}
        <div className="page-wrapper" style={{ position: 'relative', zIndex: 2, paddingTop: '140px' }}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/registro" element={<RegistrationPage />} />
            <Route path="/noticias" element={<NewsPage />} />
                                      <Route path="/dashboard" element={<DashboardPage />} />
             <Route path="/dashboard/*" element={<DashboardPage />} />
             <Route path="/:categoryId" element={<CategoryPage />} />
            {/* Aquí puedes agregar más rutas según necesites */}
          </Routes>
        </div>
        
        {/* Chat IA integrado en el hero - ChatPopup original oculto */}
        {/* <div style={{ position: 'relative', zIndex: 3 }}>
          <ChatPopup />
        </div> */}

        {/* Popup de bienvenida */}
        {showWelcomePopup && (
          <WelcomePopup 
            onClose={handleWelcomePopupClose}
          />
        )}

        {/* Modal de registro de empresa */}
        <CompanyRegistrationModal 
          isOpen={isRegistrationModalOpen} 
          onClose={() => setIsRegistrationModalOpen(false)} 
        />
      </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
